import { LuBuilding2, LuCheck, LuMonitor, LuUsers } from "@platform/icon";
import {
  EDUCATION_DELIVERY_MODE_LABEL,
  type EducationSession,
} from "@platform/billing";
import {
  formatEducationPrice,
  formatSessionDateParts,
  sessionBlockReason,
} from "../lib/education-format";

interface SessionCardProps {
  session: EducationSession;
  brandName?: string | null;
  selected: boolean;
  onToggle: (session: EducationSession) => void;
}

/** 잔여석이 이 이하로 떨어지면 숫자를 강조한다. */
const LOW_STOCK_THRESHOLD = 5;

export function SessionCard({
  session,
  brandName,
  selected,
  onToggle,
}: SessionCardProps) {
  const blocked = sessionBlockReason(session);
  const date = formatSessionDateParts(session.startAt);
  const lowStock = session.remainingCount <= LOW_STOCK_THRESHOLD;
  const DeliveryIcon =
    session.deliveryMode === "ONLINE" ? LuMonitor : LuBuilding2;

  return (
    <li
      className={`border-b border-gray-200 transition-colors last:border-b-0 dark:border-white/10 ${
        selected
          ? "bg-brand-50/70 dark:bg-brand-600/10"
          : "bg-white hover:bg-gray-50/80 dark:bg-transparent dark:hover:bg-white/[0.03]"
      }`}
    >
      {/*
        날짜 칸은 `12월 30일` 이, 잔여석 칸은 `잔여 12 / 20석` 이 각각 한 줄에
        들어가는 폭이어야 한다 — 좁히면 줄바꿈돼 두 줄로 갈라진다.
      */}
      <div className="grid gap-4 px-4 py-5 sm:grid-cols-[7rem_minmax(0,1fr)_9.5rem_auto] sm:items-center sm:px-5">
        <div className="flex items-baseline gap-2 sm:block sm:border-r sm:border-gray-200 sm:pr-5 dark:sm:border-white/10">
          <p className="whitespace-nowrap text-lg font-bold tabular-nums text-gray-950 dark:text-white">
            {date.day}
          </p>
          <p className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-neutral-400">
            {date.weekday} · {date.time}
          </p>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {brandName && (
              <span className="rounded-sm bg-gray-900 px-1.5 py-0.5 text-[11px] font-semibold text-white dark:bg-white dark:text-gray-900">
                {brandName}
              </span>
            )}
            <span className="text-xs font-semibold text-gray-500 dark:text-neutral-400">
              {session.courseTitle}
            </span>
            <span
              className="text-xs text-gray-300 dark:text-neutral-600"
              aria-hidden
            >
              /
            </span>
            <span className="text-sm font-semibold text-gray-950 dark:text-neutral-100">
              {session.title}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-neutral-400">
            <span className="inline-flex items-center gap-1.5">
              <DeliveryIcon aria-hidden className="h-3.5 w-3.5" />
              {EDUCATION_DELIVERY_MODE_LABEL[session.deliveryMode]}
              {session.venueName ? ` · ${session.venueName}` : ""}
            </span>
            {session.instructorName && (
              <span>강사: {session.instructorName}</span>
            )}
          </div>
          {blocked && (
            <p className="mt-2 text-xs font-medium text-gray-500 dark:text-neutral-400">
              {blocked}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 sm:block sm:text-right">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-neutral-400">
            <LuUsers aria-hidden className="h-3.5 w-3.5" />
            잔여{" "}
            <strong
              className={`font-semibold tabular-nums ${lowStock ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-neutral-200"}`}
            >
              {session.remainingCount} / {session.capacity}석
            </strong>
          </span>
          <p className="font-semibold tabular-nums text-gray-950 sm:mt-1 dark:text-neutral-100">
            {formatEducationPrice(session.price)}
          </p>
        </div>

        <div className="sm:text-right">
          <button
            type="button"
            disabled={!!blocked}
            aria-pressed={selected}
            aria-label={`${session.courseTitle} ${session.title} ${selected ? "선택 해제" : "선택"}`}
            onClick={() => onToggle(session)}
            className={`inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 sm:w-auto dark:disabled:bg-white/5 dark:disabled:text-neutral-500 ${
              selected
                ? "bg-brand-600 text-white hover:bg-brand-700"
                : "border border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-white/20 dark:bg-transparent dark:text-neutral-100 dark:hover:bg-white/10"
            }`}
          >
            {selected && <LuCheck aria-hidden className="h-4 w-4" />}
            {selected ? "선택됨" : "선택"}
          </button>
        </div>
      </div>
    </li>
  );
}
